import type { StrokeDTO } from '@shared/protocol/messages';
import { GRID } from './strokes';

/**
 * Paint bucket. Drawings are vector strokes (the server validates them), so a
 * fill becomes ordinary strokes: the tapped region is found on a GRID×GRID
 * raster of the drawing, then covered with zig-zag rows of a small brush.
 *
 * - Tapping empty paper fills the area closed in by ink. Small gaps (up to
 *   about 4 grid units) in the outline are bridged; if the paint still reaches
 *   the page edge it would cover the whole page, so the fill is refused.
 * - Tapping ink recolours the touching ink of (nearly) the same colour.
 */

export const FILL_BRUSH = 3;
/** Ink is any pixel at least this opaque. */
const INK_ALPHA = 40;
/** Outline gaps up to twice this are closed. */
const GAP = 2;
/** RGB distance that still counts as "the same colour" when recolouring. */
const SAME_COLOUR = 60;

export type FillResult = { strokes: StrokeDTO[]; region: Uint8Array } | { error: 'leak' | 'empty' };

/** pixels: RGBA of the drawing rendered at GRID×GRID (no line boil). */
export function fillAt(pixels: Uint8ClampedArray, x: number, y: number, color: string): FillResult {
  const N = GRID;
  const sx = Math.min(N - 1, Math.max(0, Math.floor(x)));
  const sy = Math.min(N - 1, Math.max(0, Math.floor(y)));
  const ink = new Uint8Array(N * N);
  for (let i = 0; i < N * N; i++) ink[i] = pixels[i * 4 + 3]! >= INK_ALPHA ? 1 : 0;
  const seed = sy * N + sx;

  let region: Uint8Array;
  if (ink[seed]) {
    // Recolour: flood the connected ink of the tapped colour.
    const [r, g, b] = [pixels[seed * 4]!, pixels[seed * 4 + 1]!, pixels[seed * 4 + 2]!];
    region = flood(seed, (i) => ink[i] === 1 && Math.hypot(pixels[i * 4]! - r, pixels[i * 4 + 1]! - g, pixels[i * 4 + 2]! - b) <= SAME_COLOUR);
  } else {
    // Fill paper: close small gaps by growing the ink, flood, then grow the area back up to the ink.
    const wall = dilate(ink, GAP);
    const start = wall[seed] ? nearestOpen(wall, sx, sy) : seed;
    if (start < 0) return { error: 'empty' };
    const core = flood(start, (i) => wall[i] === 0);
    if (touchesEdge(core)) return { error: 'leak' };
    region = dilate(core, GAP + 1);
    for (let i = 0; i < N * N; i++) if (ink[i]) region[i] = 0;
  }
  const strokes = coverStrokes(region, color);
  return strokes.length ? { strokes, region } : { error: 'empty' };
}

function flood(seed: number, inside: (i: number) => boolean): Uint8Array {
  const N = GRID;
  const out = new Uint8Array(N * N);
  if (!inside(seed)) return out;
  const stack = [seed];
  out[seed] = 1;
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % N;
    const next = [x > 0 ? i - 1 : -1, x < N - 1 ? i + 1 : -1, i - N, i + N];
    for (const j of next) {
      if (j < 0 || j >= N * N || out[j] || !inside(j)) continue;
      out[j] = 1;
      stack.push(j);
    }
  }
  return out;
}

/** Square dilation by r pixels (two separable passes). */
function dilate(src: Uint8Array, r: number): Uint8Array {
  const N = GRID;
  const tmp = new Uint8Array(N * N);
  const out = new Uint8Array(N * N);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      if (!src[y * N + x]) continue;
      for (let k = Math.max(0, x - r); k <= Math.min(N - 1, x + r); k++) tmp[y * N + k] = 1;
    }
  }
  for (let x = 0; x < N; x++) {
    for (let y = 0; y < N; y++) {
      if (!tmp[y * N + x]) continue;
      for (let k = Math.max(0, y - r); k <= Math.min(N - 1, y + r); k++) out[k * N + x] = 1;
    }
  }
  return out;
}

/** A tap that lands on the grown ink (thin areas): the nearest open pixel within a few units. */
function nearestOpen(wall: Uint8Array, x: number, y: number): number {
  const N = GRID;
  for (let r = 1; r <= GAP + 2; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && ny >= 0 && nx < N && ny < N && !wall[ny * N + nx]) return ny * N + nx;
      }
    }
  }
  return -1;
}

function touchesEdge(a: Uint8Array): boolean {
  const N = GRID;
  for (let k = 0; k < N; k++) {
    if (a[k] || a[(N - 1) * N + k] || a[k * N] || a[k * N + N - 1]) return true;
  }
  return false;
}

/**
 * Covers a region with brush rows FILL_BRUSH-1 apart. Runs on neighbouring
 * rows that overlap are chained into one zig-zag stroke (fewer strokes, so
 * fills stay inside the stroke and point limits).
 */
function coverStrokes(region: Uint8Array, color: string): StrokeDTO[] {
  const N = GRID;
  const step = FILL_BRUSH - 1;
  type Chain = { p: number[]; last: [number, number]; atRight: boolean; row: number };
  const done: Chain[] = [];
  let open: Chain[] = [];
  for (let y = 1; y < N; y += step) {
    const runs: [number, number][] = [];
    let start = -1;
    for (let x = 0; x <= N; x++) {
      const on = x < N && region[y * N + x] === 1;
      if (on && start < 0) start = x;
      if (!on && start >= 0) {
        runs.push([start, x - 1]);
        start = -1;
      }
    }
    const next: Chain[] = [];
    for (const [a, b] of runs) {
      const ci = open.findIndex((c) => c.row === y - step && c.last[0] <= b && c.last[1] >= a);
      if (ci >= 0) {
        const c = open.splice(ci, 1)[0]!;
        // Turn inside both runs, then sweep the other way.
        const end = c.atRight ? c.last[1] : c.last[0];
        const turn = Math.min(b, Math.max(a, end));
        c.p.push(turn, y, c.atRight ? a : b, y);
        c.atRight = !c.atRight;
        c.last = [a, b];
        c.row = y;
        next.push(c);
      } else {
        next.push({ p: [a, y, b, y], last: [a, b], atRight: true, row: y });
      }
    }
    done.push(...open);
    open = next;
  }
  done.push(...open);
  return done.map((c) => ({ c: color, s: FILL_BRUSH, e: false, p: c.p.map((v) => Math.min(GRID, Math.max(0, v))) }));
}

/**
 * Paint bucket for pixel art: the same rules on a grid of cell×cell squares
 * (a square with any ink in it is ink, so thin pencil outlines still hold the
 * paint); the result is one pixel stroke.
 */
export function pixelFillAt(pixels: Uint8ClampedArray, x: number, y: number, cell: number, color: string): FillResult {
  const C = Math.floor(GRID / cell);
  // Per square: the offset of its first inked pixel (its colour), or -1 for bare paper.
  const inkAt = new Int32Array(C * C).fill(-1);
  for (let py = 0; py < C * cell; py++) {
    for (let px = 0; px < C * cell; px++) {
      const o = (py * GRID + px) * 4;
      const i = Math.floor(py / cell) * C + Math.floor(px / cell);
      if (inkAt[i]! < 0 && pixels[o + 3]! >= INK_ALPHA) inkAt[i] = o;
    }
  }
  const at = (i: number): number => inkAt[i]!;
  const ink = (i: number): boolean => inkAt[i]! >= 0;
  const cx = Math.min(C - 1, Math.max(0, Math.floor(x / cell)));
  const cy = Math.min(C - 1, Math.max(0, Math.floor(y / cell)));
  const seed = cy * C + cx;
  const s0 = at(seed);
  const inside = ink(seed)
    ? (i: number): boolean => ink(i) && Math.hypot(pixels[at(i)]! - pixels[s0]!, pixels[at(i) + 1]! - pixels[s0 + 1]!, pixels[at(i) + 2]! - pixels[s0 + 2]!) <= SAME_COLOUR
    : (i: number): boolean => !ink(i);
  const region = new Uint8Array(C * C);
  const stack = [seed];
  region[seed] = 1;
  const p: number[] = [];
  while (stack.length) {
    const i = stack.pop()!;
    const ix = i % C;
    const iy = Math.floor(i / C);
    // Paper reaching the page edge would paint the whole background.
    if (!ink(seed) && (ix === 0 || iy === 0 || ix === C - 1 || iy === C - 1)) return { error: 'leak' };
    p.push(ix * cell, iy * cell);
    for (const n of [ix > 0 ? i - 1 : -1, ix < C - 1 ? i + 1 : -1, iy > 0 ? i - C : -1, iy < C - 1 ? i + C : -1]) {
      if (n >= 0 && !region[n] && inside(n)) {
        region[n] = 1;
        stack.push(n);
      }
    }
  }
  return { strokes: [{ c: color, s: cell, e: false, b: 'pixel', p }], region };
}

/** The same stroke reflected across the page's vertical centre line (pixel squares keep their corner on the left). */
export function mirrorStroke(s: StrokeDTO): StrokeDTO {
  const w = s.b === 'pixel' ? s.s : 0;
  return { ...s, p: s.p.map((v, i) => (i % 2 === 0 ? GRID - v - w : v)) };
}
